import { api } from "@/convex/_generated/api";
import { useDeferredPublicEnhancement } from "@/hooks/use-deferred-public-enhancement";
import { isSupportedLocale } from "@/i18n-locales";
import i18n from "@/i18n";
import { useQuery } from "convex/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export function addCmsResource(
  locale: string,
  namespace: string,
  key: string,
  value: unknown,
) {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    for (const [childKey, childValue] of Object.entries(value)) {
      addCmsResource(locale, namespace, `${key}.${childKey}`, childValue);
    }
    return;
  }
  i18n.addResource(locale, namespace, key, value as never);
}

export function CmsTranslationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { i18n: activeI18n } = useTranslation();
  const locale = isSupportedLocale(activeI18n.language)
    ? activeI18n.language
    : "it";
  const ready = useDeferredPublicEnhancement();
  const rows = useQuery(api.cms.publicBundle, ready ? { locale } : "skip");

  useEffect(() => {
    if (!rows) return;
    for (const row of rows) {
      let value: unknown = row.value;
      if (row.valueType === "json") {
        try {
          value = JSON.parse(row.value);
        } catch {
          continue;
        }
      }
      // JSON objects from the CMS are often intentionally partial. Apply
      // their leaves one by one so a value such as auth.error.generic does
      // not remove the bundled auth.error.reference/invalidCredentials keys.
      addCmsResource(locale, row.namespace, row.key, value);
    }
    void i18n.changeLanguage(locale);
  }, [locale, rows]);

  return <>{children}</>;
}
